import { reportErrorToSentry } from '../../../configuration/setup/sentry';

export type ProductConfig = {
    iconName?: string;
    freeTrial?: FreeTrialProductConfig;
    termsOfServiceUrl?: string;
    legacyTermsOfServiceUrl?: string;
    applicationLink?: string;
    marketplaceLink?: string;
    redirectUrl?: string;
    descriptionOfServiceUrl?: string;
    legacyDescriptionOfServiceUrl?: string;
    levelConfig?: Record<string, LevelConfig>;
};

export type FreeTrialProductConfig = {
    productName: string;
    level: string;
};

export type LevelConfig = {
    redirectUrl: string;
    descriptionOfServiceUrl: string;
    legacyDescriptionOfServiceUrl?: string;
    termsOfServiceUrl?: string;
    legacyTermsOfServiceUrl?: string;
};

export const LOCALE_PLACEHOLDER = 'LOCALE';

export function replaceLocalePlaceholder(url: string, locale: string) {
    return url.replace(LOCALE_PLACEHOLDER, locale.substring(0, 2));
}

const OPENMARKETPLACE_BASE_URL = `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/`;
const DOCUMENT_UPDATE_CUTOFF_TIMESTAMP = Date.parse('2026-09-01T00:00:00.000Z');
const EARLY_DOCUMENT_UPDATE_SKU = 'rio-sku00000058';

function selectDocumentUrl(currentUrl: string, legacyUrl: string | undefined, sku: string, timestamp?: string): string {
    if (Date.now() < DOCUMENT_UPDATE_CUTOFF_TIMESTAMP && sku.toLowerCase() !== EARLY_DOCUMENT_UPDATE_SKU) {
        return currentUrl;
    }

    const parsedTimestamp = timestamp === undefined ? Date.now() : Date.parse(timestamp);

    if (Number.isNaN(parsedTimestamp)) {
        reportErrorToSentry(new Error(`Called document URL lookup with invalid timestamp ${timestamp}`));
        return currentUrl;
    }

    return parsedTimestamp < DOCUMENT_UPDATE_CUTOFF_TIMESTAMP ? (legacyUrl ?? currentUrl) : currentUrl;
}

const PRODUCT_CONFIG_BY_ID: Record<string, ProductConfig> = {
    // Regular services
    'man-sku00000004': {
        applicationLink: 'https://remotedownload.rio.cloud/#archive',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/compliant-m',
        iconName: 'Compliant',
        freeTrial: {
            productName: 'Compliant M',
            level: '21',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/legacy`,
        levelConfig: {
            '20-ft': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
            '21': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
            '22': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
            '22-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
            '23-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
            '24': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
            '26-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/compliant-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/m/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/m/legacy`,
            },
        },
    },
    'man-sku00000005': {
        applicationLink: 'https://perform.rio.cloud/#fleet',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/perform',
        iconName: 'Perform',
        freeTrial: {
            productName: 'Perform',
            level: '15',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/perform/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/perform/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/perform`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/perform/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/perform/dos/legacy`,
    },
    'man-sku00000026': {
        iconName: 'Jupiter',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-now/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-now`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-now/dos`,
    },
    'rio-sku00000027': {
        applicationLink: 'https://drivingtime.rio.cloud/#realtime',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/timed',
        iconName: 'Timed',
        freeTrial: {
            productName: 'Timed',
            level: '15',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/timed`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed/dos/legacy`,
    },
    'rio-sku00000047': {
        applicationLink: 'https://livemonitor.rio.cloud/#map',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/geo',
        iconName: 'Geo',
        freeTrial: {
            productName: 'Geo',
            level: '15',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/geo`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/dos/legacy`,
        levelConfig: {
            '34': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/geo-l`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/dos/l`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/dos/l/legacy`,
                termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/tos/l`,
                legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo/tos/l/legacy`,
            },
        },
    },
    'man-sku00000048': {
        iconName: 'One_Minute_Locator',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/one-minute-locator/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/one-minute-locator`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/one-minute-locator/dos`,
    },
    'man-sku00000049': {
        applicationLink: 'https://manservicecare.rio.cloud/',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/man-servicecare-m',
        iconName: 'MAN_ServiceCare',
        freeTrial: {
            productName: 'MAN ServiceCare M',
            level: '21',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/tos/s-m`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/tos/s-m/legacy`,
        levelConfig: {
            '10': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/s/legacy`,
            },
            '14': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/s/legacy`,
            },
            '15': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/s/legacy`,
            },
            '20': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '20-ft': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '21': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '22': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '22-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '23-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '24': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
            '26-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-servicecare-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-servicecare/dos/m/legacy`,
            },
        },
    },
    'man-sku00000050': {
        applicationLink: 'https://ota-charging.rio.cloud/#overview',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/man-emanager-m',
        iconName: 'MAN_eManager',
        freeTrial: {
            productName: 'MAN eManager M',
            level: '25',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/tos/s-m`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/tos/s-m/legacy`,
        levelConfig: {
            '10': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/s/legacy`,
            },
            '14': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/s/legacy`,
            },
            '15': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/s/legacy`,
            },
            '20-ft': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
            '20': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
            '21': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
            '22': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
            '23-io': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
            '24': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
            '25': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m/legacy`,
            },
        },
    },
    'kor-sku00000053': {
        iconName: 'MAN_ServiceCare',
        termsOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/tos/s-m',
        legacyTermsOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/tos/s-m/legacy',
        descriptionOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/dos/s',
        legacyDescriptionOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/dos/s/legacy',
        redirectUrl: 'https://rio.cloud/en/marketplace/produkt/man-servicecare-s',
    },
    'rio-sku00000055': {
        iconName: 'Geo_Data_Flow',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo-data-flow/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo-data-flow/tos/legacy`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo-data-flow/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/geo-data-flow/dos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/geo-data-flow`,
    },
    'rio-sku00000056': {
        iconName: 'Timed_Data_Flow',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed-data-flow/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed-data-flow/tos/legacy`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed-data-flow/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/timed-data-flow/dos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/timed-data-flow`,
    },
    'man-sku00000061': {
        iconName: 'MinimalDataSet',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/minimaldatenset/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-emanager-m`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-emanager/dos/m`,
    },
    'man-sku00000066': {
        applicationLink: 'https://simplepay.rio.cloud/',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/man-simplepay',
        iconName: 'Jupiter',
        freeTrial: {
            productName: 'MAN SimplePay',
            level: '15',
        },
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-simplepay`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/dos/legacy`,
    },
    'man-sku00000073': {
        applicationLink: 'https://rio.cloud/en/marketplace/produkt/man-datapackage-tigr/next-steps',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/man-datapackage-tigr',
        iconName: 'DataProvision',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/tos/s-tigr`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/tos/s-tigr/legacy`,
        levelConfig: {
            '10': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-datapackage-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/s/legacy`,
            },
            '14': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-datapackage-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/s/legacy`,
            },
            '15': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-datapackage-s`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/s`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/s/legacy`,
            },
            '20': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-datapackage-tigr`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/tigr`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/tigr/legacy`,
            },
            '24': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-datapackage-tigr`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/tigr`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/tigr/legacy`,
            },
            '25': {
                redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-datapackage-tigr`,
                descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/tigr`,
                legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-datapackage/dos/tigr/legacy`,
            },
        },
    },
    'man-sku00000081': {
        applicationLink: 'https://jupiter.rio.cloud/',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/man-simplepay',
        iconName: 'Jupiter',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-simplepay`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-simplepay/dos/legacy`,
    },
    'man-sku00000085': {
        applicationLink: 'https://livemonitor.rio.cloud/',
        marketplaceLink: 'https://rio.cloud/en/marketplace/produkt/man-smartroute',
        iconName: 'SmartRoutePlanning',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-smartroute/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/man-smartroute`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-smartroute/dos`,
    },
    'rio-sku00000094': {
        applicationLink: 'https://ordercom.cartright.rio.cloud/#/welcome',
        marketplaceLink: 'https://rio.cloud/en/documents/order-communication/how-to/user-manual',
        iconName: 'OrderCommunication',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/order-communication/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/order-communication`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/order-communication/dos`,
    },
    'man-sku00000098': {
        iconName: 'Compliant',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/tos/legacy`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/compliant/dos/legacy`,
    },
    'zaf-sku00000100': {
        iconName: 'MAN_ServiceCare',
        termsOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/tos/s-m',
        legacyTermsOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/tos/s-m/legacy',
        redirectUrl: 'https://rio.cloud/en/marketplace/produkt/man-servicecare-s',
        descriptionOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/dos/s',
        legacyDescriptionOfServiceUrl: 'https://rio.cloud/en/documents/man-servicecare/dos/s/legacy',
    },
    'rio-sku00000101': {
        iconName: 'Fleet', // Workaround until actual icon is available
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/order-communication/tos`,
    },
    'rio-sku00000102': {
        iconName: 'Staff', // Workaround until actual icon is available
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/order-communication/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/order-communication`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/order-communication/dos`,
    },
    // MAN Now features / subscriptions
    'man-sku00000052': {
        iconName: 'MAN-Now-MapUpdate',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-now/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/order-communication`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/order-communication/dos`,
    },
    'man-sku00000060': {
        iconName: 'MAN-Now-OnlineTraffic',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-now/tos`,
    },
    'man-sku00000072': {
        iconName: 'MAN-Now-EfficientRoute',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-now/tos`,
    },
    'man-sku00000076': {
        iconName: 'MAN-Now-OnlineNews',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/man-now/tos`,
    },
    // Test products
    'rio-sku00000051': {
        iconName: 'Maintenance',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/cypress-connect`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/dos`,
    },
    'rio-sku00000058': {
        iconName: 'Maintenance',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/tos`,
        legacyTermsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/tos/legacy`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/cypress-connect`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/dos`,
        legacyDescriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/dos/legacy`,
    },
    'rio-sku00000059': {
        iconName: 'Maintenance',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/cypress-connect`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/dos`,
    },
    'rio-sku00000071': {
        iconName: 'Staff',
        termsOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/tos`,
        redirectUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/marketplace/produkt/cypress-connect`,
        descriptionOfServiceUrl: `https://rio.cloud/${LOCALE_PLACEHOLDER}/documents/cypress-connect/dos`,
    },
};

export function getFreeTrialConfig(sku: string): FreeTrialProductConfig | undefined {
    return PRODUCT_CONFIG_BY_ID[sku.toLowerCase()]?.freeTrial;
}

export function getRedirectConfig(locale: string, sku: string, level?: string): string | undefined {
    const productConfig = PRODUCT_CONFIG_BY_ID[sku.toLowerCase()];

    if (productConfig?.levelConfig && level && productConfig.levelConfig[level]) {
        return replaceLocalePlaceholder(productConfig.levelConfig[level].redirectUrl, locale);
    }

    if (productConfig?.redirectUrl) {
        return replaceLocalePlaceholder(productConfig.redirectUrl, locale);
    }

    return;
}

export function getProductIconUrl(sku: string): string | undefined {
    const productIconName = PRODUCT_CONFIG_BY_ID[sku.toLowerCase()]?.iconName;

    if (!productIconName) {
        return;
    }

    return `https://rio.cloud/fileadmin/Marketplace/AppIcons/${productIconName}.svg`;
}

export function getApplicationLink(sku?: string): string | undefined {
    if (!sku) {
        return;
    }

    return PRODUCT_CONFIG_BY_ID[sku.toLowerCase()]?.applicationLink;
}

export function getOpenMarketplaceProductLink(sku?: string): string | undefined {
    if (!sku) {
        return;
    }

    return PRODUCT_CONFIG_BY_ID[sku.toLowerCase()]?.marketplaceLink;
}

export function getOpenMarketplaceLink(): string {
    return replaceLocalePlaceholder(OPENMARKETPLACE_BASE_URL, 'en-GB');
}

export function getLocalizedOpenMarketplaceLink(locale: string): string {
    return replaceLocalePlaceholder(OPENMARKETPLACE_BASE_URL, locale);
}

export function getDescriptionOfServiceUrl(locale: string, sku: string, level?: string, timestamp?: string) {
    const productConfig = PRODUCT_CONFIG_BY_ID[sku.toLowerCase()];
    const levelConfig = level ? productConfig?.levelConfig?.[level] : undefined;

    if (levelConfig) {
        const url = selectDocumentUrl(
            levelConfig.descriptionOfServiceUrl,
            levelConfig.legacyDescriptionOfServiceUrl,
            sku,
            timestamp
        );
        return replaceLocalePlaceholder(url, locale);
    }

    if (productConfig?.redirectUrl && productConfig?.descriptionOfServiceUrl) {
        const url = selectDocumentUrl(
            productConfig.descriptionOfServiceUrl,
            productConfig.legacyDescriptionOfServiceUrl,
            sku,
            timestamp
        );
        return replaceLocalePlaceholder(url, locale);
    }

    return;
}

export const getTermsAndConditionsUrl = (locale: string, sku: string, level?: string, timestamp?: string) => {
    const productConfig = PRODUCT_CONFIG_BY_ID[sku.toLowerCase()];
    const genericTos = productConfig?.termsOfServiceUrl;
    const levelConfig = level ? productConfig?.levelConfig?.[level] : undefined;
    const levelSpecificTos = levelConfig?.termsOfServiceUrl;

    if (levelSpecificTos) {
        const url = selectDocumentUrl(levelSpecificTos, levelConfig.legacyTermsOfServiceUrl, sku, timestamp);
        return replaceLocalePlaceholder(url, locale);
    }
    if (genericTos) {
        const url = selectDocumentUrl(genericTos, productConfig.legacyTermsOfServiceUrl, sku, timestamp);
        return replaceLocalePlaceholder(url, locale);
    }
    reportErrorToSentry(
        new Error(
            `Called terms and condition for sku ${sku} and level ${level}, but neither specific nor generic tos are configured`
        )
    );
    return;
};
